import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../contexts/KStackAuthContext';
import ThemeToggle from '../components/ThemeToggle';
import './Login.css';

interface VerifyEmailRouterState {
  email?: string;
  info?: string;
}

/**
 * Email-verification page (Phase F4, 2026-05-25).
 *
 * Reached either from /signup (state-injected email + info banner) or
 * directly via /verify-email by a user who already has a code. Posts to
 * Nexus's POST /auth/verify-email with { email, code }.
 *
 * Anti-enum: invalid emails and wrong codes both return INVALID_CODE on
 * the server. We surface "invalid or expired" in both cases.
 *
 * On success we navigate to /login with a state-info toast so the user
 * sees clear confirmation in the place they need to act next (sign in).
 */
const VerifyEmail: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state ?? {}) as VerifyEmailRouterState;

  const [email, setEmail] = useState(state.email || '');
  const [code, setCode] = useState('');
  const [info, setInfo] = useState<string | null>(state.info || null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [resendBusy, setResendBusy] = useState(false);

  const { verifyEmail, resendVerification } = useAuth();

  // If the page was opened cold (no state, user typed the URL) the
  // email input starts empty. Otherwise pre-fill it but keep it
  // editable in case the user mistyped on signup.
  useEffect(() => {
    if (state.email && !email) setEmail(state.email);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrorMsg(null);
    if (!email || code.length !== 6) {
      setErrorMsg('Enter the 6-digit code we emailed to your address.');
      return;
    }
    setIsLoading(true);
    try {
      await verifyEmail(email, code);
      navigate('/login', {
        state: {
          info: 'Email verified — sign in with your new account.',
        },
      });
    } catch (err) {
      setErrorMsg(
        err instanceof Error ? err.message : 'Failed to verify code.',
      );
    } finally {
      setTimeout(() => setIsLoading(false), 100);
    }
  };

  const handleResend = async () => {
    if (!email) {
      setErrorMsg('Enter your email first to resend the code.');
      return;
    }
    setResendBusy(true);
    setInfo(null);
    setErrorMsg(null);
    await resendVerification(email);
    setInfo(
      'If an account exists for that email and is awaiting verification, a new code is on its way.',
    );
    setResendBusy(false);
  };

  return (
    <div className="login-container">
      <ThemeToggle placement="corner" />
      <div className="login-box">
        <div className="login-header">
          <h1>Verify your email</h1>
        </div>
        <p>
          Enter the 6-digit code we just emailed you. Codes expire after 15
          minutes.
        </p>

        {info && (
          <div className="banner-info">
            <p>{info}</p>
          </div>
        )}

        {errorMsg && (
          <div className="error-modal" onClick={() => setErrorMsg(null)}>
            <div className="error-content" onClick={(e) => e.stopPropagation()}>
              <div className="error-message">❌ {errorMsg}</div>
              <button
                className="error-ok-button"
                onClick={() => setErrorMsg(null)}
              >
                OK
              </button>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="login-form">
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isLoading}
              autoComplete="email"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="code">Verification code</label>
            <input
              type="text"
              id="code"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              disabled={isLoading}
              placeholder="6-digit code"
              autoComplete="one-time-code"
              required
            />
          </div>

          <button
            type="submit"
            disabled={isLoading || code.length !== 6}
            className="btn-primary"
          >
            {isLoading ? 'Verifying…' : 'Verify email'}
          </button>
        </form>

        <button
          type="button"
          onClick={handleResend}
          disabled={resendBusy}
          className="btn-secondary-link"
          style={{ marginTop: 12 }}
        >
          {resendBusy ? 'Sending…' : 'Resend code'}
        </button>

        <div className="divider">
          <span>or</span>
        </div>

        <Link to="/login" className="btn-secondary-link">
          Back to sign in
        </Link>
      </div>
    </div>
  );
};

export default VerifyEmail;
